import React, { useState } from "react";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

const fabricanteLabel = (f) => (f?.nome ? `${f.nome} (${f.ref})` : f?.ref || "");

const formatarDataHora = (valor) => {
  if (!valor) return "";

  const d = new Date(valor);
  if (Number.isNaN(d.getTime())) return "";

  return d.toLocaleString("pt-PT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

export default function DialogProcessarCampanhasFabricante({
  PaperComponent,
  open,
  onClose,
  fabricantesVigentes,
  fabricanteVigenteSelecionado,
  setFabricanteVigenteSelecionado,
  fabricanteVigenteInput,
  setFabricanteVigenteInput,
  diagnosticoFabricante,
  processamentosCampanhas,
  carregarDiagnosticoFabricante,
  processarCampanhasFabricante,
}) {
  const campanhas = diagnosticoFabricante?.campanhas || [];
  const intervalo = diagnosticoFabricante?.intervaloglobal;
  const [aProcessar, setAProcessar] = useState(false);
  const haProcessamentoEmCurso = (processamentosCampanhas || []).some(
    (p) => p.estado === "Em processamento"
  );

  const handleProcessar = async () => {
    try {
      setAProcessar(true);
      await processarCampanhasFabricante();
    } finally {
      setAProcessar(false);
    }
  };

  return (
    <Dialog
      PaperComponent={PaperComponent}
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          width: { xs: "calc(100% - 24px)", lg: "1100px" },
          maxWidth: "100%",
          m: { xs: 1.5, md: 3 },
        },
      }}
    >
      <DialogTitle id="draggable-dialog-title" style={{ cursor: "move" }}>
        Processar campanhas por fabricante
      </DialogTitle>

      <DialogContent
        sx={{
          px: { xs: 1.5, md: 3 },
          maxWidth: "100%",
          overflowX: "hidden",
        }}
      >
        <MDBox display="grid" gap={2} mt={1}>
          <MDBox
            p={{ xs: 1.25, md: 2 }}
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: 2,
              maxWidth: "100%",
              overflowX: "auto",
            }}
          >
            <MDTypography variant="h6" mb={1}>
              Fabricante
            </MDTypography>

            <Autocomplete
              size="small"
              options={fabricantesVigentes}
              inputValue={fabricanteVigenteInput}
              getOptionLabel={fabricanteLabel}
              value={fabricanteVigenteSelecionado}
              onInputChange={(_, value) => {
                setFabricanteVigenteInput(value);
              }}
              onChange={async (_, value) => {
                setFabricanteVigenteSelecionado(value);
                setFabricanteVigenteInput(value ? fabricanteLabel(value) : "");

                if (value?.ref) {
                  await carregarDiagnosticoFabricante(value.ref);
                }
              }}
              renderInput={(params) => (
                <TextField {...params} placeholder="Selecionar fabricante..." />
              )}
            />

            {intervalo && (
              <MDBox mt={2}>
                <MDTypography variant="caption" sx={{ display: "block" }}>
                  <b>Intervalo global a analisar:</b>{" "}
                  {String(intervalo.datainicial || "").substring(0, 10)} até{" "}
                  {String(intervalo.datafinal || "").substring(0, 10)}
                </MDTypography>
              </MDBox>
            )}
          </MDBox>

          <MDBox
            p={{ xs: 1.25, md: 2 }}
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: 2,
              maxWidth: "100%",
              overflowX: "auto",
            }}
          >
            <MDTypography variant="h6" mb={1}>
              Campanhas pendentes
            </MDTypography>

            <MDBox
              sx={{
                maxHeight: { xs: "45vh", md: 360 },
                overflow: "auto",
                maxWidth: "100%",
              }}
            >
              <table
                style={{
                  width: "100%",
                  minWidth: 820,
                  borderCollapse: "collapse",
                }}
              >
                <thead>
                  <tr>
                    <th style={thLeft}>Campanha</th>
                    <th style={thLeft}>Código</th>
                    <th style={thLeft}>Data inicial</th>
                    <th style={thLeft}>Data final</th>
                    <th style={thRight}>Faturas</th>
                    <th style={thRight}>Pendentes</th>
                    <th style={thLeft}>Estado</th>
                  </tr>
                </thead>

                <tbody>
                  {campanhas.map((c) => (
                    <tr key={c.id}>
                      <td style={tdBase}>{c.nome}</td>
                      <td style={tdBase}>{c.codigo_campanha}</td>
                      <td style={tdBase}>{String(c.datainicial || "").substring(0, 10)}</td>
                      <td style={tdBase}>{String(c.datafinal || "").substring(0, 10)}</td>
                      <td style={tdRight}>{c.total_faturas}</td>
                      <td style={tdRight}>{c.total_faturas_pendentes}</td>
                      <td style={tdBase}>{c.estado_calculado}</td>
                    </tr>
                  ))}

                  {fabricanteVigenteSelecionado && campanhas.length === 0 && (
                    <tr>
                      <td colSpan={7} style={{ fontSize: 12, padding: 8, color: "#7b809a" }}>
                        Não existem campanhas pendentes para este fabricante.
                      </td>
                    </tr>
                  )}

                  {!fabricanteVigenteSelecionado && (
                    <tr>
                      <td colSpan={7} style={{ fontSize: 12, padding: 8, color: "#7b809a" }}>
                        Selecione um fabricante para consultar as campanhas pendentes.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </MDBox>
          </MDBox>

          <MDBox
            p={{ xs: 1.25, md: 2 }}
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: 2,
              maxWidth: "100%",
              overflowX: "auto",
            }}
          >
            <MDTypography variant="h6" mb={1}>
              Últimos processamentos
            </MDTypography>

            <MDBox
              sx={{
                maxHeight: { xs: "40vh", md: 240 },
                overflow: "auto",
                maxWidth: "100%",
              }}
            >
              <table
                style={{
                  width: "100%",
                  minWidth: 980,
                  borderCollapse: "collapse",
                }}
              >
                <thead>
                  <tr>
                    <th style={thRight}>ID</th>
                    <th style={thLeft}>Iniciado</th>
                    <th style={thLeft}>Terminado</th>
                    <th style={thRight}>Analisado</th>
                    <th style={thRight}>Detetado</th>
                    <th style={thRight}>Atualizado</th>
                    <th style={thRight}>Erros</th>
                    <th style={thLeft}>Estado</th>
                    <th style={thLeft}>Mensagem</th>
                  </tr>
                </thead>

                <tbody>
                  {(processamentosCampanhas || []).map((p) => (
                    <tr key={p.id}>
                      <td style={tdRight}>{p.id}</td>
                      <td style={tdBase}>{formatarDataHora(p.iniciado_em)}</td>
                      <td style={tdBase}>{formatarDataHora(p.terminado_em)}</td>
                      <td style={tdRight}>{p.total_analisado}</td>
                      <td style={tdRight}>{p.total_detetado}</td>
                      <td style={tdRight}>{p.total_atualizado}</td>
                      <td style={tdRight}>{p.total_erros}</td>
                      <td style={tdBase}>{p.estado}</td>
                      <td style={tdBase}>{p.mensagem}</td>
                    </tr>
                  ))}

                  {(!processamentosCampanhas || processamentosCampanhas.length === 0) && (
                    <tr>
                      <td colSpan={9} style={{ fontSize: 12, padding: 8, color: "#7b809a" }}>
                        Ainda não existem processamentos registados.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </MDBox>
          </MDBox>
        </MDBox>
      </DialogContent>

      <DialogActions
        sx={{
          flexWrap: "wrap",
          gap: 1,
          px: { xs: 2, md: 3 },
          pb: { xs: 2, md: 2 },
          "& .MuiButton-root": {
            width: { xs: "100%", sm: "auto" },
          },
        }}
      >
        <MDButton color="secondary" onClick={onClose}>
          Fechar
        </MDButton>

        <MDButton
          color="success"
          disabled={
            !fabricanteVigenteSelecionado?.ref ||
            campanhas.length === 0 ||
            haProcessamentoEmCurso ||
            aProcessar
          }
          onClick={handleProcessar}
        >
          {haProcessamentoEmCurso || aProcessar ? "A processar..." : "Processar"}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

const thBase = {
  position: "sticky",
  top: 0,
  zIndex: 3,
  background: "#fff",
  boxShadow: "0 2px 4px rgba(0,0,0,0.06)",
  fontSize: 12,
  padding: 6,
};

const thLeft = {
  ...thBase,
  textAlign: "left",
};

const thRight = {
  ...thBase,
  textAlign: "right",
};

const tdBase = {
  fontSize: 12,
  padding: 6,
  borderTop: "1px solid #eee",
};

const tdRight = {
  ...tdBase,
  textAlign: "right",
};
